import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { z } from 'zod';
import { RootState } from '../reducers';
import { addTagToUserThunk } from '../features/settings/settingsSlice';

// Define the feature flag schema
export const featureFlagSchema = z.object({
  feature_enable_onboarding: z.enum(['true', 'false']),
  feature_unguard_upload: z.enum(['true', 'false']),
  feature_unguard_import_url: z.enum(['true', 'false']),
  feature_upgrade_variant_v2: z.enum(['true', 'false']),
});

// Derive TypeScript types from Zod schema
export type FeatureFlagKey = keyof z.infer<typeof featureFlagSchema>;
export type FeatureFlagValue = z.infer<
  typeof featureFlagSchema
>[FeatureFlagKey];

// Helper function to generate random boolean
const getRandomBoolean = () => Math.random() >= 0.5;

// Helper function to check if a key is a feature flag
const isFeatureFlag = (key: string): key is FeatureFlagKey => {
  return key.startsWith('feature_');
};

export const useBooleanFeature = () => {
  const dispatch = useDispatch();
  const tags = useSelector((state: RootState) => state.settings.user.tags);

  const getFeature = useCallback(
    (key: string): boolean => {
      // Winner: feature_enable_onboarding = false (disable onboarding for everyone)
      if (key === 'feature_enable_onboarding') {
        return false;
      }

      // If it's a defined feature flag
      if (isFeatureFlag(key)) {
        // If the feature flag exists in tags, return its value
        if (key in tags) {
          return tags[key] === 'true';
        }

        // If the feature flag doesn't exist, calculate a random value and set it
        const randomValue = getRandomBoolean();
        const stringValue = randomValue.toString() as FeatureFlagValue;
        dispatch(addTagToUserThunk({ key, value: stringValue }));
        return randomValue;
      }

      // For undefined feature flags, return random boolean without storing
      return getRandomBoolean();
    },
    [dispatch, tags]
  );

  return { getFeature };
};

export default useBooleanFeature;
